import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingBag, Plus, Loader2, Sparkles, Tag, Check, ShieldCheck } from "lucide-react";
import { fetchProductByTitle, addLineToSharedCart, applyDiscountCode, previewBundle, money } from "@/lib/shopify";

const HONEY_TITLE = process.env.REACT_APP_SHOPIFY_HONEY_TITLE;
const GHEE_TITLE = process.env.REACT_APP_SHOPIFY_GHEE_TITLE;
const COMBO_CODE = process.env.REACT_APP_SHOPIFY_COMBO_CODE || "";

export default function ComboOfferCard() {
  const [items, setItems] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([fetchProductByTitle(HONEY_TITLE), fetchProductByTitle(GHEE_TITLE)])
      .then(async ([honey, ghee]) => {
        if (!alive) return;
        setItems([honey, ghee]);
        const p = await previewBundle([honey.firstVariant.id, ghee.firstVariant.id], COMBO_CODE).catch(() => null);
        if (alive) setPreview(p);
      })
      .catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, []);

  if (!items || items.length < 2) return null;
  const [honey, ghee] = items;
  const available = honey.availableForSale && ghee.availableForSale;
  const listTotal = Number(honey.price.amount) + Number(ghee.price.amount);
  const hasSaving = preview && preview.codeApplicable && preview.saving > 0;
  const payTotal = hasSaving ? preview.subtotal - preview.saving : listTotal;

  const addBundle = async (checkout) => {
    setBusy(checkout ? "buy" : "add");
    try {
      await addLineToSharedCart(honey.firstVariant.id, 1);
      let cart = await addLineToSharedCart(ghee.firstVariant.id, 1);
      if (COMBO_CODE) cart = (await applyDiscountCode(COMBO_CODE)) || cart;
      if (checkout) { window.location.assign(cart.checkoutUrl); return; }
      toast.success(hasSaving ? `Bundle added — you save ${money(preview.saving, preview.currency)}` : "Honey + Ghee bundle added to your cart", {
        action: { label: "Checkout", onClick: () => window.location.assign(cart.checkoutUrl) },
      });
    } catch (e) {
      toast.error(e.message || "Unable to add the bundle. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-[28px] bg-[#143C18] text-white border border-[#D4A017]/30 shadow-[0_30px_60px_-25px_rgba(20,60,24,0.6)]"
      data-testid="combo-offer-card"
    >
      <div className="absolute inset-0 grain opacity-60 pointer-events-none" />
      <div className="grid lg:grid-cols-12 relative">
        <div className="lg:col-span-5 p-6 sm:p-8 lg:p-10 flex items-center justify-center">
          <div className="relative w-full max-w-[380px] aspect-[5/4]">
            <img src={honey.featuredImage?.url} alt="Rudrima pure forest honey 500g jar" width="300" height="300" loading="lazy" decoding="async" className="absolute left-0 top-0 w-[60%] aspect-square object-cover rounded-2xl border-4 border-[#FFF8E7] shadow-2xl -rotate-3" />
            <img src={ghee.featuredImage?.url} alt="Rudrima pure A2 cow ghee 500g jar" width="300" height="300" loading="lazy" decoding="async" className="absolute right-0 bottom-0 w-[60%] aspect-square object-cover rounded-2xl border-4 border-[#FFF8E7] shadow-2xl rotate-3" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#D4A017] text-[#143C18] flex items-center justify-center shadow-lg z-10"><Plus className="w-6 h-6" strokeWidth={3} /></div>
          </div>
        </div>

        <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10 lg:pl-0 flex flex-col justify-center">
          <div className="chip chip-gold !bg-[#D4A017] !text-[#143C18] !border-transparent mb-4 w-fit"><Sparkles className="w-3.5 h-3.5" /> Bundle &amp; Save</div>
          <h3 className="font-display text-3xl sm:text-4xl font-bold leading-tight">The Complete <span className="italic font-serif-alt text-[#E8BE4A]">Kitchen Pair</span></h3>
          <p className="mt-3 text-white/80 leading-relaxed max-w-lg">Pure Forest Honey (500g) and Pure A2 Cow Ghee (500g) together — everything your everyday kitchen rituals need, added to your cart in one tap.</p>

          <ul className="mt-4 space-y-1.5 text-sm text-white/85">
            {[honey.title, ghee.title].map((t) => <li key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D4A017]" strokeWidth={3} /> {t}</li>)}
          </ul>

          <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#E8BE4A] font-bold">Bundle price</div>
              <div className="font-display text-3xl sm:text-4xl font-bold flex items-baseline gap-3" data-testid="combo-price">
                {money(payTotal, honey.currency)}
                {hasSaving && <span className="text-base font-body font-medium text-white/50 line-through">{money(listTotal, honey.currency)}</span>}
              </div>
              {hasSaving ? (
                <div className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E8BE4A]" data-testid="combo-saving"><Tag className="w-4 h-4" /> You save {money(preview.saving, preview.currency)} · code {COMBO_CODE} applied automatically</div>
              ) : (
                <div className="mt-1 text-xs text-white/60" data-testid="combo-no-saving">Both products · one checkout</div>
              )}
            </div>
            <div className="text-xs text-white/60 inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[#D4A017]" /> Secure Shopify checkout</div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
            <button onClick={() => addBundle(true)} disabled={!available || !!busy} data-testid="combo-buy-now-btn" className="btn-gold !py-3.5 justify-center disabled:opacity-60">
              {busy === "buy" ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><ShoppingBag className="w-4 h-4" /> Buy Bundle Now · {money(payTotal, honey.currency)}</>}
            </button>
            <button onClick={() => addBundle(false)} disabled={!available || !!busy} data-testid="combo-add-to-cart-btn" className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#143C18] !py-3.5 justify-center disabled:opacity-60">
              {busy === "add" ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</> : <><Plus className="w-4 h-4" /> Add Both to Cart</>}
            </button>
          </div>
          {!available && <p className="mt-3 text-xs text-[#E8BE4A]">One of the products is currently out of stock — the bundle will be back soon.</p>}
        </div>
      </div>
    </motion.div>
  );
}
